import { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'
import {
  CbamSimulationGood,
  useGetAllCbamSimulationGoodsForOrganization
} from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import { useSimulationWithLiability } from '@/features/cbam-simulations-v2/hooks/useSimulationWithLiabilty'
import { useGetAllSupplierEmissionsReportsForOrganization } from '@/features/suppliers/hooks/supplierEmissionsReport'
import {
  buildLiabilityOverviewModel,
  LiabilityOverviewTemplate
} from '@/shared/components/cbam-good-details/liability-overview'
import { LoadingScreen } from '@/shared/components'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { getDefaultCbamModifiers } from '@/shared/types/cbamModifiers'

/** Default simulation projection year (matches `CbamSimulationDetailsPage`). */
const DEFAULT_SIMULATION_YEAR = 2026

export default function SimulationGoodLiabilityOverviewPdf() {
  const { simulationGoodId } = useParams<{ simulationGoodId: string }>()
  const { search } = useLocation()
  const { t, handleChangeLanguage } = useI18n()
  const lang = useMemo(() => new URLSearchParams(search).get('lang'), [search])
  const simulationYear = useMemo(() => {
    const raw = new URLSearchParams(search).get('year')
    if (raw == null || raw === '') {
      return DEFAULT_SIMULATION_YEAR
    }
    const n = Number.parseInt(raw, 10)
    return Number.isFinite(n) ? n : DEFAULT_SIMULATION_YEAR
  }, [search])

  const { session } = useSession()
  const organizationId = session.organization.id

  const simulationGoodsQuery = useGetAllCbamSimulationGoodsForOrganization(organizationId)
  const supplierEmissionsReportsQuery =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)
  const { data: goodsCodes } = useGoodsCodes({ aggregatedGoodsCategories: undefined })

  useEffect(() => {
    document.body.style.setProperty('background-color', 'white')
  }, [])

  useEffect(() => {
    if (lang) {
      handleChangeLanguage(lang)
    }
  }, [lang, handleChangeLanguage])

  const simulationGood = useMemo((): CbamSimulationGood | null => {
    if (!simulationGoodId || !simulationGoodsQuery.data) {
      return null
    }
    return simulationGoodsQuery.data.find(g => g.id === simulationGoodId) ?? null
  }, [simulationGoodId, simulationGoodsQuery.data])

  const simulationId = simulationGood?.cbamSimulationId ?? '__pending__'

  const {
    liabilityDataBaseLow,
    simulation,
    isBatchLiabilityLoading,
    isCbamSimulationGoodsLoading
  } = useSimulationWithLiability(organizationId, simulationId, { productionMethodCode: '' })

  const supplierEmissionsReports = supplierEmissionsReportsQuery.data ?? []

  const liabilityRow = useMemo((): BatchLiabilityResultItem | null => {
    if (!simulationGood) {
      return null
    }
    const results = liabilityDataBaseLow?.results ?? []
    return results.find(r => r.id === simulationGood.id) ?? null
  }, [simulationGood, liabilityDataBaseLow?.results])

  const supplierEmissionsReport = useMemo(() => {
    if (!simulationGood?.supplierEmissionsReportId) {
      return null
    }
    return (
      supplierEmissionsReports.find(r => r.id === simulationGood.supplierEmissionsReportId) ?? null
    )
  }, [simulationGood, supplierEmissionsReports])

  const modifiers = simulation?.modifiers ?? getDefaultCbamModifiers()

  const aggregatedGoodsCategory = useMemo(() => {
    if (!simulationGood) {
      return undefined
    }
    return goodsCodes?.find(gc => gc.goodsCode === simulationGood.cnCode)
      ?.aggregatedGoodsCategory
  }, [simulationGood, goodsCodes])

  const model = useMemo(() => {
    if (!simulationGood || !liabilityRow) {
      return null
    }
    return buildLiabilityOverviewModel({
      liabilityData: liabilityRow,
      good: {
        quarter: simulationGood.quarter,
        cnCode: simulationGood.cnCode,
        description: simulationGood.description,
        netMassInTonnes: simulationGood.netMassInTonnes,
        isUsingDefaultEmissions: simulationGood.isUsingDefaultEmissions,
        country: simulationGood.country
      },
      supplierEmissionsReport,
      modifiers,
      projectionYear: simulationYear,
      aggregatedGoodsCategory,
      t
    })
  }, [
    simulationGood,
    liabilityRow,
    supplierEmissionsReport,
    modifiers,
    simulationYear,
    aggregatedGoodsCategory,
    t
  ])

  const isInitialLoading =
    simulationGoodsQuery.isLoading ||
    supplierEmissionsReportsQuery.isLoading ||
    isCbamSimulationGoodsLoading

  if (isInitialLoading) {
    return <LoadingScreen />
  }

  if (simulationGood && isBatchLiabilityLoading) {
    return <LoadingScreen />
  }

  return (
    <div className="min-h-screen bg-white page-padding print:mt-0">
      <LiabilityOverviewTemplate
        model={model}
        originCountryCode={simulationGood?.country ?? ''}
      />
      {/* do not remove the below line as it is needed for PDF */}
      <div data-artem="render-completed" />
    </div>
  )
}
